//#region Imports
import React, { useState, useEffect, useRef } from "react";
import { StyleSheet, Platform, SafeAreaView, FlatList, View } from "react-native";
import { useTheme } from "@react-navigation/native";
import { useAccountContext } from "../../hooks/useAccountContext";
import { useAuthContext } from "../../hooks/useAuthContext";
import CustomButton from "../../components/CustomButton";
import CustomLabel from "../../components/CustomLabel/CustomLabel";
import MyTimesheetsScreen from '../SelfService/Timesheets/MyTimesheetsScreen';
import { useMyTimesheets } from "../../hooks/useMyTimesheets";
import { generateUniqueID } from "../../components/AssessmentBuilderCmponent/CreateGroup/CreateGroupFunctions";
import { useTranslation } from "react-i18next";
import MyTimesheetsDetailsScreen from "../SelfService/Timesheets/MyTimesheetsDetailsScreen";
import TimesheetApprovalScreen from "../SelfService/Timesheets/TimesheetApprovalScreen";
import RoleGuard from "../../components/RoleGuard";
import { TASKS, TASK_OPTIONS } from "../../constants/Tasks";
import { OASYS_CONST } from "../../constants/const";
import TimesheetOnBehalfOf from "../SelfService/Timesheets/TimesheetOnBehalfOf";
import { showMessage } from "react-native-flash-message";
//#endregion

const HomePageTimesheets = ({ route }) => {
  //#region Const Definitions
  const { colors } = useTheme();
  const styles = makeStyles(colors);
  const { username } = useAuthContext();
  const { account } = useAccountContext();
  const { t } = useTranslation();
  const { getEType, getTSDates, loadTimeScheme, loadNewTimesheet, loadExpCode,
    loadExpenseLines, loadUserTimesheets, loadNonChargeableLines, loadNextTimesheet,
    loadChargeableLines, loadTimesheetHelper, loadTimesheetInfo } = useMyTimesheets();
  const [isMonthlyTimesheet, setIsMonthlyTimesheet] = useState(false);
  const [isWeeklyTimesheet, setIsWeeklyTimesheet] = useState(false);
  const [startDate, setStartDate] = useState(0);
  const [endDate, setEndDate] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  /* All screens related to timesheets */
  const buttonTable = [1, 2, 3, 4];
  const showNewTimesheetScreenRef = useRef(null);
  const showMyTimesheetsScreenRef = useRef(null);
  const showMyTimesheetDetailsScreenRef = useRef(null);
  const showTimesheetApprovalScreenRef = useRef(null);
  const showTimesheetOnBehalfOfScreenRef = useRef(null);
  const [showNewTimesheetScreen, setShowNewTimesheetScreen] = useState(0);
  const [showMyTimesheetDetailsScreen, setShowMyTimesheetDetailsScreen] = useState(0);
  const [showMyTimesheetsScreen, setShowMyTimesheetsScreen] = useState(1);
  const [showTimesheetApprovalScreen, setShowTimesheetApprovalScreen] = useState(0);
  const [showTimesheetOnBehalfOfScreen, setShowTimesheetOnBehalfOfScreen] = useState(0);

  /* Data kept and passed to screens */
  const tsUIRef = useRef(null);
  const [userTimesheetsList, setUserTimesheetsList] = useState([]);
  const [timesheetChargableList, setTimesheetChargableList] = useState([]);
  const [timesheetNonChargableList, setTimesheetNonChargableList] = useState([]);
  const [tsExpenses, setTSExpenses] = useState([]);
  const [expCode, setExpCode] = useState([]);
  const [tsUI, setTSUI] = useState(null);
  const [isApprover, setisApprover] = useState(false);
  const [timesheetInfo, setTimesheetInfo] = useState({});
  const [timeScheme, setTimeScheme] = useState({});
  const [isEmbedded, setIsEmbedded] = useState(false);

  // Helper for dropdowns variables
  const [jobCodeChargOptions, setJobCodeChargOptions] = useState([]);
  const [jobCodeNonChargOptions, setJobCodeNonChargOptions] = useState([]);
  const [usersJobCodeChargOptions, setUsersJobCodeChargOptions] = useState([]);
  const [usersJobCodeNonChargOptions, setUsersJobCodeNonChargOptions] = useState([]);
  const [usersChargPhaseCodeOptions, setUsersChargPhaseCodeOptions] = useState([]);
  const [usersNonChargPhaseCodeOptions, setUsersNonChargPhaseCodeOptions] = useState([]);
  const [usersExpCodeOptions, setUsersExpCodeOptions] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [yearOptions, setYearOptions] = useState([]);

  //#region SelectScreens
  const removeAllscreens = () => {
    setShowNewTimesheetScreen(0);
    setShowMyTimesheetDetailsScreen(0);
    setShowTimesheetApprovalScreen(0);
    setShowMyTimesheetsScreen(0);
    setShowTimesheetOnBehalfOfScreen(0);
  }

  const selectMyTimesheetsScreen = () => {
    removeAllscreens();
    setShowMyTimesheetsScreen(1);
  };

  const selectNewTimesheetScreen = () => {
    setShowNewTimesheetScreen(1);
    removeAllscreens();
  };

  const selectMyTimesheetDetailsScreen = () => {
    removeAllscreens();
    setShowMyTimesheetDetailsScreen(1);
  };

  const selectTimesheetApprovalScreen = () => {
    removeAllscreens();
    setShowTimesheetApprovalScreen(1);
  };

  const selectTimesheetOnBehalfOfScreen = () => {
    removeAllscreens();
    setShowTimesheetOnBehalfOfScreen(1);
  };
  //#endregion

  //#region Load Data
  const loadNextTS = () => {
    if (account) {
      let obj = [account.employeeId, generateUniqueID()]
      loadNextTimesheet(obj)
        .then((userData) => {
          if(userData === ''){
            showMessage({
              message: t("common:etypenotFound"),
              type: "error",
              duration: 3000,
            });
            return;
          }
          setTSUI(userData)
          selectMyTimesheetDetailsScreen()
        })
        .catch((error) => {
          console.error(error);
        });
    }
  };

  const loadNewTS = (year, month, period, week) => {
    if (account) {
      let obj = [generateUniqueID(), year, month, period, week, isWeeklyTimesheet]
      loadNewTimesheet(obj)
        .then((userData) => {
          setTSUI(userData)
          selectMyTimesheetDetailsScreen()
        })
        .catch((error) => {
          console.error(error);
        });
    }
  };

  const loadTSHelper = () => {
    loadTimesheetHelper(username)
      .then((userData) => {
        setJobCodeChargOptions(userData.JobCodeCharg || []);
        setJobCodeNonChargOptions(userData.JobCodeNonCharg || []);
        setYearOptions(userData.YearOptions || []);
        setCompanies(userData.CompanyOptions || [])
        setUsersJobCodeChargOptions(userData.UsersJobCodeCharg || [])
        setUsersJobCodeNonChargOptions(userData.UsersJobCodeNonCharg || [])
        setUsersChargPhaseCodeOptions(userData.UsersChargPhaseCode || []);
        setUsersNonChargPhaseCodeOptions(userData.UsersNonChargPhaseCode || []);
        setUsersExpCodeOptions(userData.UsersJobCodeExp || [])
      })
      .catch((error) => {
        console.error(error);
      });
    loadExpCode(username)
      .then((userData) => {
        setExpCode(userData)
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const loadUserTS = () => {
    if (account) {
      loadUserTimesheets(account.employeeId)
        .then((userData) => {
          setUserTimesheetsList(userData);
        })
        .catch((error) => {
          console.error(error);
        });
    }
  };

  const loadTSDetails = (tsnoUI) => {
    if (account) {
      if (isMonthlyTimesheet) {
        Promise.all([
          loadNonChargeableLines(tsnoUI.tsnoUIA),
          loadNonChargeableLines(tsnoUI.tsnoUIB),
          loadChargeableLines(tsnoUI.tsnoUIA),
          loadChargeableLines(tsnoUI.tsnoUIB),
          loadExpenseLines(tsnoUI.tsnoUIA),
          loadExpenseLines(tsnoUI.tsnoUIB)
        ])
          .then(([nonChargA, nonChargB, chargA, chargB, expA, expB]) => {
            const chargComp = {
              periodA: chargA,
              periodB: chargB
            };
            const nonChargComp = {
              periodA: nonChargA,
              periodB: nonChargB
            };
            const expComp = {
              periodA: expA,
              periodB: expB
            };
            setTimesheetNonChargableList(nonChargComp);
            setTimesheetChargableList(chargComp);
            setTSExpenses(expComp);
          })
          .catch((error) => {
            console.error('Error loading timesheet info:', error);
          });
      } else {
        loadNonChargeableLines(tsnoUI)
          .then((userData) => {
            setTimesheetNonChargableList(userData);
          })
          .catch((error) => {
            console.error(error);
          });
        loadChargeableLines(tsnoUI)
          .then((userData) => {
            setTimesheetChargableList(userData);
          })
          .catch((error) => {
            console.error(error);
          });
        loadExpenseLines(tsnoUI)
          .then((userData) => {
            setTSExpenses(userData)
          })
          .catch((error) => {
            console.error(error);
          });
      }
    }
  };

  const loadTSInfo = (tsnoUI) => {
    if (account) {
      if (isMonthlyTimesheet) {
        Promise.all([
          loadTimesheetInfo(tsnoUI.tsnoUIA),
          loadTimesheetInfo(tsnoUI.tsnoUIB),
          loadTimeScheme(isMonthlyTimesheet ? tsnoUI.tsnoUIA : tsnoUI),
          loadTimeScheme(isMonthlyTimesheet ? tsnoUI.tsnoUIB : tsnoUI)
        ])
          .then(([userDataA, userDataB, timeschemeA, timeschemeB]) => {
            const combinedData = [{
              periodA: userDataA,
              periodB: userDataB
            }];

            const combinedScheme = {
              periodA: timeschemeA,
              periodB: timeschemeB
            }

            setTimeScheme(combinedScheme);
            setTimesheetInfo(combinedData);
          })
          .catch((error) => {
            console.error('Error loading timesheet info:', error);
          });
      } else {
        loadTimesheetInfo(tsnoUI)
          .then((userData) => {
            setTimesheetInfo(userData);
          })
          .catch((error) => {
            console.error(error);
          });
        loadTimeScheme(tsnoUI)
          .then((userData) => {
            setTimeScheme(userData[0]);
          })
          .catch((error) => {
            console.error(error);
          });
      }

    }
  };
  //#endregion

  //#region UseEffect
  useEffect(() => {
    showMyTimesheetsScreenRef.current = showMyTimesheetsScreen;
  }, [showMyTimesheetsScreen]);

  useEffect(() => {
    showTimesheetOnBehalfOfScreenRef.current = showTimesheetOnBehalfOfScreen;
  }, [showTimesheetOnBehalfOfScreen]);

  useEffect(() => {
    setIsLoading(true)
    getTSDates(username).then((res) => {
      setStartDate(res[0].timesheet_startDate)
      setEndDate(res[0].timesheet_endDate)

      getEType(username).then((res) => {
        setIsMonthlyTimesheet(res[0].etype === OASYS_CONST.MONTHLY_EMPL_TYPE_TS ? true : false);
        setIsWeeklyTimesheet(res[0].etype === OASYS_CONST.WEEKLY_EMPL_TYPE_TS ? true : false);
        loadTSHelper();
        setIsLoading(false);
      })
        .catch((error) => {
          console.error(error);
        })
    }).catch((error) => {
      console.error(error);
    })
    setIsLoading(false)
  }, [account])

  // Handles refresh in web
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      try {
        localStorage.setItem("showMyTimesheetsScreen", showMyTimesheetsScreenRef.current);
        localStorage.setItem("showTimesheetOnBehalfOfScreen", showTimesheetOnBehalfOfScreenRef.current);
        localStorage.setItem('isRefresh', "true");
        localStorage.setItem('lastVisitedScreen', 'HomePageTimesheets');
      } catch (error) {
        console.log(error.message);
      };
    };

    if (Platform.OS === 'web') {
      window.addEventListener('beforeunload', handleBeforeUnload);
      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    };
  }, []);

  const checkIfRefreshed = () => {
    try {
      const isRefresh = localStorage.getItem("isRefresh");
      if (isRefresh === "true") {
        localStorage.setItem("isRefresh", "false");
        setShowMyTimesheetsScreen(
          parseInt(localStorage.getItem("showMyTimesheetsScreen"))
        );
        setShowTimesheetOnBehalfOfScreen(
          parseInt(localStorage.getItem("showTimesheetOnBehalfOfScreen"))
        );
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (Platform.OS === "web") {
      checkIfRefreshed();
    }
    //setShowMyTimesheetsScreen(1);
  }, []);
  //#endregion

  //#region Loading
  if (isLoading)
    return (
      <View style={styles.loader}>
        <ActivityIndicator animating={true} color={colors.blue} />
      </View>
    )
  //#endregion

  return (
    <SafeAreaView style={styles.main}>
      {/* SubMenu */}
      <View>
        <FlatList
          horizontal
          showsHorizontalScrollIndicator={true}
          directionalLockEnabled={true}
          alwaysBounceVertical={false}
          data={buttonTable}
          contentContainerStyle={{
            alignSelf: "flex-start",
            marginBottom: "1%",
            marginLeft: "2%",
          }}
          showsVerticalScrollIndicator={true} //shows horizontal scroll bar on web
          renderItem={({ index }) => {
            return (
              <SafeAreaView>
                {index == 1 && (
                  <RoleGuard parentTask={TASKS.ALLEMPLOYEETASKS} task={TASK_OPTIONS.TIMESHEETHOME} requireCRUD>
                    <CustomButton
                      onPress={() => {
                        selectMyTimesheetsScreen();
                      }}
                      text={t("navigate:MT")}
                      iconName="logo-buffer"
                      show={showMyTimesheetsScreen || showMyTimesheetDetailsScreen ? "HOME_SELECTED" : "HOME"}
                      type={showMyTimesheetsScreen || showMyTimesheetDetailsScreen ? "HOME_SELECTED" : "HOME"}
                      textTop="0"
                      sz={Platform.OS !== "web" ? 20 : 25}
                      isGradient={showMyTimesheetsScreen || showMyTimesheetDetailsScreen ? true : false}
                    />
                  </RoleGuard>
                )}
                {index == 2 && (
                  <RoleGuard parentTask={[TASKS.UTILS, TASKS.UTILSADMIN]} task={TASK_OPTIONS.TIMESHEETAPPROVAL} requireCRUD>
                    <CustomButton
                      onPress={() => {
                        selectTimesheetApprovalScreen();
                      }}
                      text={t("navigate:TSA")}
                      iconName="log-out-outline"
                      show={showTimesheetApprovalScreen ? "HOME_SELECTED" : "HOME"}
                      type={showTimesheetApprovalScreen ? "HOME_SELECTED" : "HOME"}
                      textTop="0"
                      isGradient={showTimesheetApprovalScreen ? true : false}
                      sz={Platform.OS !== "web" ? 20 : 25}
                    />
                  </RoleGuard>
                )}
                {index == 3 && (
                  <RoleGuard parentTask={TASKS.DELIGATE} task={TASK_OPTIONS.TIMESHEETHOME} requireCRUD>
                    <CustomButton
                      onPress={() => {
                        selectTimesheetOnBehalfOfScreen();
                      }}
                      text={t("navigate:TTS")}
                      iconName="person-add-outline"
                      show={showTimesheetOnBehalfOfScreen ? "HOME_SELECTED" : "HOME"}
                      type={showTimesheetOnBehalfOfScreen ? "HOME_SELECTED" : "HOME"}
                      textTop="0"
                      sz={Platform.OS !== "web" ? 20 : 25}
                      isGradient={showTimesheetOnBehalfOfScreen ? true : false}
                    />
                  </RoleGuard>
                )}
              </SafeAreaView>
            )
          }} />
      </View>

      {
        showMyTimesheetsScreen ? (
          <SafeAreaView style={[styles.submain]}>
            <CustomLabel value={t("common:MyTimesheets")} type="MINORTITLE" />
            <MyTimesheetsScreen
              isMonthlyTimesheet={isMonthlyTimesheet}
              isWeekly={isWeeklyTimesheet}
              selectNewTimesheetScreen={selectNewTimesheetScreen}
              selectMyTimesheetDetailsScreen={selectMyTimesheetDetailsScreen}
              loadUserTimesheets={loadUserTS}
              loadNextTimesheet={loadNextTS}
              loadNewTimesheet={loadNewTS}
              yearOptions={yearOptions}
              userTimesheetsList={userTimesheetsList}
              setTSUI={setTSUI}
              setisApprover={setisApprover}
            />
          </SafeAreaView>
        ) : (
          <></>
        )
      }

      {
        showMyTimesheetDetailsScreen ? (
          <MyTimesheetsDetailsScreen
            isMonthlyTimesheet={isMonthlyTimesheet}
            isWeekly={isWeeklyTimesheet}
            startDate={startDate}
            endDate={endDate}
            selectMyTimesheetsScreen={selectMyTimesheetsScreen}
            loadTimesheetDetails={loadTSDetails}
            loadTimesheetInfo={loadTSInfo}
            tsUI={tsUI}
            isApprover={isApprover}
            timesheetNonChargableList={timesheetNonChargableList}
            timesheetChargableList={timesheetChargableList}
            tsExpenses={tsExpenses}
            expCode={expCode}
            jobCodeChargOptions={jobCodeChargOptions}
            jobCodeNonChargOptions={jobCodeNonChargOptions}
            usersJobCodeChargOptions={usersJobCodeChargOptions}
            usersJobCodeNonChargOptions={usersJobCodeNonChargOptions}
            usersChargPhaseCodeOptions={usersChargPhaseCodeOptions}
            usersNonChargPhaseCodeOptions={usersNonChargPhaseCodeOptions}
            setUsersNonChargPhaseCodeOptions={setUsersNonChargPhaseCodeOptions}
            setUsersChargPhaseCodeOptions={setUsersChargPhaseCodeOptions}
            usersExpCodeOptions={usersExpCodeOptions}
            companiesOptions={companies}
            tsInfo={timesheetInfo}
            timeScheme={timeScheme}
            userTimesheetsList={userTimesheetsList}
            setTSInfo={setTimesheetInfo}
            isEmbedded={isEmbedded}
          />
        ) : (
          <></>
        )
      }

      {
        showTimesheetApprovalScreen ? (
          <TimesheetApprovalScreen
            setIsMonthlyTimesheet={setIsMonthlyTimesheet}
            setIsWeeklyTimesheet={setIsWeeklyTimesheet}
            selectMyTimesheetDetailsScreen={selectMyTimesheetDetailsScreen}
            setTSUI={setTSUI}
            setisApprover={setisApprover}
          />
        ) : (
          <></>
        )
      }

      {
        showTimesheetOnBehalfOfScreen ? (
          <TimesheetOnBehalfOf
            setStartDate={setStartDate}
            setEndDate={setEndDate}
            setIsMonthlyTimesheet={setIsMonthlyTimesheet}
            setIsWeeklyTimesheet={setIsWeeklyTimesheet}
            setShowMyTimesheetDetailsScreen={setShowMyTimesheetDetailsScreen}
            setTSUI={setTSUI}
            setisApprover={setisApprover}
            loadNewTimesheet={loadNewTS}
            setIsEmbedded={setIsEmbedded}
            isEmbedded={isEmbedded}
            setTimesheetNonChargableList={setTimesheetNonChargableList}
            setTimesheetChargableList={setTimesheetChargableList}
            isMonthlyTimesheet={isMonthlyTimesheet}
            isWeeklyTimesheet={isWeeklyTimesheet}
            startDate={startDate}
            endDate={endDate}
            selectMyTimesheetsScreen={selectMyTimesheetsScreen}
            loadTimesheetDetails={loadTSDetails}
            loadTimesheetInfo={loadTSInfo}
            tsUI={tsUI}
            isApprover={isApprover}
            timesheetNonChargableList={timesheetNonChargableList}
            timesheetChargableList={timesheetChargableList}
            tsExpenses={tsExpenses}
            expCode={expCode}
            jobCodeChargOptions={jobCodeChargOptions}
            jobCodeNonChargOptions={jobCodeNonChargOptions}
            usersJobCodeChargOptions={usersJobCodeChargOptions}
            usersJobCodeNonChargOptions={usersJobCodeNonChargOptions}
            usersChargPhaseCodeOptions={usersChargPhaseCodeOptions}
            usersNonChargPhaseCodeOptions={usersNonChargPhaseCodeOptions}
            setUsersNonChargPhaseCodeOptions={setUsersNonChargPhaseCodeOptions}
            setUsersChargPhaseCodeOptions={setUsersChargPhaseCodeOptions}
            usersExpCodeOptions={usersExpCodeOptions}
            companiesOptions={companies}
            tsInfo={timesheetInfo}
            timeScheme={timeScheme}
            userTimesheetsList={userTimesheetsList}
            setTSInfo={setTimesheetInfo}
          />
        ) : (
          <></>
        )
      }
    </SafeAreaView>
  )
};

const makeStyles = (colors, width) => StyleSheet.create({
  submain: {
    flex: 1,
    borderRadius: 35,
    height: "100%",
    elevation: Platform.OS === "web" ? 3 : 0,
    shadowColor: colors.card_line,
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.1,
    shadowRadius: Platform.OS === "web" ? 5 : 0,
    backgroundColor: colors.background_card,
  },
  main: {
    flex: 1,
    marginHorizontal: 10,
    marginTop: "1%",
  },
});

export default HomePageTimesheets;